import React from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import { AtsLockContext } from 'components/common/AtsLock'

import { useSendTestsInvitationsModal } from 'components/modals/SendTestsInvitationsModal/hooks'
import { useSendTestsRemindersModal } from 'components/modals/SendTestsRemindersModal/hooks'
import { jobs } from 'orm'
import {
  useCandidateActions,
  useCandidateGroupActions,
  useCandidateState,
  useJobDetailsTab,
  useFilter,
} from './../hooks'
import { groupForSending } from 'utils/sending'

const useJobInvitesTabData = state => {
  const { projectId } = state

  const data = useSelector(state => state.orm.candidates)
  const job = useSelector(state => jobs.getItem()(state, { pk: projectId }))

  const { items, meta: { selected } = {} } = data ?? {}

  const { invitees, remindees, expired } = React.useMemo(
    () => groupForSending(items, selected),
    [items, selected],
  )

  const deadline = R.propOr('', 'deadline', job)

  const isNoInvitees = R.isEmpty(invitees)
  const isNoRemindees = R.isEmpty(remindees)
  const isNoExpired = R.isEmpty(expired)

  return {
    state: {
      invitees,
      remindees,
      expired,
      deadline,
      isNoInvitees,
      isNoRemindees,
      isNoExpired,
    },
  }
}

export const useJobInvitesTab = state => {
  const { projectId } = state

  const { isAtsLocked } = React.useContext(AtsLockContext)

  const status = ['created', 'invited']

  const {
    state: {
      invitees,
      remindees,
      expired,
      deadline,
      isNoInvitees,
      isNoRemindees,
      isNoExpired,
    },
  } = useJobInvitesTabData({ projectId })

  const {
    state: { count, search },
    handlers: { setSearch },
  } = useJobDetailsTab()

  const {
    state: { candidate, selected, hasMoveCandidatesFeature },
    handlers: { setCandidate },
  } = useCandidateState()

  const {
    state: { filter },
    handlers: { setFilter },
  } = useFilter()

  const {
    handleSendTestsInvitationsModalClose,
    handleSendTestsInvitationsModalConfirm,
    handleSendTestsInvitationsModalOpen,
    isSendTestsInvitationsModalOpen,
  } = useSendTestsInvitationsModal(
    { projectId },
    { handleSetCandidate: setCandidate },
  )

  const {
    handleSendTestsRemindersModalClose,
    handleSendTestsRemindersModalConfirm,
    handleSendTestsRemindersModalOpen,
    isSendTestsRemindersModalOpen,
  } = useSendTestsRemindersModal(
    { projectId },
    { handleSetCandidate: setCandidate },
  )

  const {
    state: {
      isCandidateManagementModalOpen,
      isDeleteCandidateModalOpen,
      isMoveCandidateModalOpen,
    },
    handlers: {
      getActions,
      handleFavorite,
      handleCandidateManagementModalClose,
      handleCandidateManagementModalConfirm,
      handleDeleteCandidateModalClose,
      handleDeleteCandidateModalConfirm,
      handleMoveCandidateModalClose,
      handleMoveCandidateModalConfirm,
    },
  } = useCandidateActions(
    { candidate, projectId, status, hasMoveCandidatesFeature, isAtsLocked },
    { handleSetCandidate: setCandidate },
  )

  const {
    state: {
      actions,
      isDeleteCandidateGroupModalOpen,
      isExportCandidateGroupModalOpen,
      isMoveCandidateGroupModalOpen,
    },
    handlers: {
      handleDeleteCandidateGroupModalClose,
      handleDeleteCandidateGroupModalConfirm,
      handleExportCandidateGroupModalClose,
      handleMoveCandidateGroupModalClose,
      handleMoveCandidateGroupModalConfirm,
    },
  } = useCandidateGroupActions({
    projectId,
    selected,
    status,
    hasMoveCandidatesFeature,
    isAtsLocked,
  })

  const [isTargetingExpired, setIsTargetingExpired] = React.useState(false)

  // Closing the modal already clears the candidate (see the modal's own `handleReset`), so
  // these only have to say which group is being sent to.
  const handleSendRemindersOpen = React.useCallback(() => {
    setIsTargetingExpired(false)
    handleSendTestsRemindersModalOpen()
  }, [handleSendTestsRemindersModalOpen])

  const handleSendExpiredOpen = React.useCallback(() => {
    setIsTargetingExpired(true)
    handleSendTestsRemindersModalOpen()
  }, [handleSendTestsRemindersModalOpen])

  const handleSendTestsMessage = React.useCallback(
    ({ candidate }) => {
      setCandidate(candidate)
      R.equals(candidate?.status, 'created')
        ? handleSendTestsInvitationsModalOpen()
        : handleSendTestsRemindersModalOpen()
    },
    [
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalOpen,
      setCandidate,
    ],
  )

  return {
    state: {
      actions,
      candidate,
      count,
      deadline,
      invitees,
      remindees,
      expired,
      search,
      hasMoveCandidatesFeature,
      isAtsLocked,
      filter,
      isCandidateManagementModalOpen,
      isDeleteCandidateModalOpen,
      isMoveCandidateModalOpen,
      isDeleteCandidateGroupModalOpen,
      isExportCandidateGroupModalOpen,
      isMoveCandidateGroupModalOpen,
      isSendTestsInvitationsModalOpen,
      isSendTestsRemindersModalOpen,
      isNoInvitees,
      isNoRemindees,
      isNoExpired,
      isTargetingExpired,
    },
    handlers: {
      getActions,
      setFilter,
      handleFavorite,
      handleCandidateManagementModalClose,
      handleCandidateManagementModalConfirm,
      handleDeleteCandidateModalClose,
      handleDeleteCandidateModalConfirm,
      handleMoveCandidateModalClose,
      handleMoveCandidateModalConfirm,
      handleDeleteCandidateGroupModalClose,
      handleDeleteCandidateGroupModalConfirm,
      handleExportCandidateGroupModalClose,
      handleMoveCandidateGroupModalClose,
      handleMoveCandidateGroupModalConfirm,
      handleSendTestsInvitationsModalClose,
      handleSendTestsInvitationsModalConfirm,
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalClose,
      handleSendTestsRemindersModalConfirm,
      handleSendTestsRemindersModalOpen,
      handleSendRemindersOpen,
      handleSendExpiredOpen,
      handleSendTestsMessage,
      setSearch,
    },
  }
}
